import React, { useState } from "react";
import { Box, Paper, Tabs, Tab, Typography } from "@mui/material";
import { getLoggedInUserInfo, filterConsultationTypesForUser } from "./calendarHelper";

const Calendar = ({ initialSettings }) => {
  const { isSuperOrStoreAdmin, userEmail, userId, userRole } = getLoggedInUserInfo();

  const allConsultationTypes = initialSettings?.consultationTypes || initialSettings?.googleConfig?.consultationTypes || [];
  const defaultName = (initialSettings?.calendarProvider === 'outlook' ? initialSettings?.outlookConfig?.name : initialSettings?.googleConfig?.name) ||
    initialSettings?.defaultConsultationType?.name ||
    initialSettings?.googleConfig?.name ||
    initialSettings?.outlookConfig?.name ||
    initialSettings?.name ||
    "General Consultation (Default)";
  const defaultUrl = initialSettings?.googleConfig?.calendarUrl || initialSettings?.calendarUrl || "";

  // Filter Consultation Types assigned to THIS logged-in user / role (and active)
  const visibleConsultationTypesWithIndex = allConsultationTypes.map((type, originalIdx) => {
    if (type.isActive === false) return null;
    if (isSuperOrStoreAdmin) return { type, originalIdx };

    const managerEmail = (type.assignedManagerEmail || "").toLowerCase();
    const managerId = (type.assignedManagerId || "").toString();
    const managerRole = (type.assignedManagerRole || "").toLowerCase();
    const calEmail = (type.calendarEmail || "").toLowerCase();

    const isMatch = (userEmail && (managerEmail === userEmail || calEmail === userEmail)) ||
                    (userId && managerId === userId) ||
                    (userRole && managerRole === userRole);

    return isMatch ? { type, originalIdx } : null;
  }).filter(Boolean);

  const showDefaultTab = isSuperOrStoreAdmin || visibleConsultationTypesWithIndex.length === 0;

  // Initial tab index selection:
  // If showDefaultTab is true: select 0 (Default Global Calendar URL)
  // Else select first assigned consultation type's originalIdx + 1
  const initialIndex = React.useMemo(() => {
    if (showDefaultTab) return 0;
    return visibleConsultationTypesWithIndex.length > 0 ? (visibleConsultationTypesWithIndex[0].originalIdx + 1) : 0;
  }, [showDefaultTab, visibleConsultationTypesWithIndex]);

  const [selectedTabIndex, setSelectedTabIndex] = useState(initialIndex);

  // Active iframe URL resolution
  const activeCalendarUrl = React.useMemo(() => {
    if (selectedTabIndex === 0) {
      return defaultUrl;
    }
    const typeObj = allConsultationTypes[selectedTabIndex - 1];
    return typeObj?.calendarUrl || defaultUrl;
  }, [selectedTabIndex, allConsultationTypes, defaultUrl]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
      {/* Consultation Type Tabs for Embedded Iframe */}
      {(showDefaultTab || visibleConsultationTypesWithIndex.length > 0) && (
        <Paper sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#f8fafc', px: 1 }}>
          <Tabs
            value={selectedTabIndex}
            onChange={(e, val) => setSelectedTabIndex(val)}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            {showDefaultTab && <Tab label={defaultName} value={0} />}
            {visibleConsultationTypesWithIndex.map(({ type, originalIdx }) => (
              <Tab key={originalIdx} label={type.name || `Type ${originalIdx + 1}`} value={originalIdx + 1} />
            ))}
          </Tabs>
        </Paper>
      )}

      {/* Embedded Google Calendar Iframe */}
      <Box
        sx={{
          height: "80vh",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: 3,
          width: "100%",
          bgcolor: "background.paper",
        }}
      >
        {activeCalendarUrl ? (
          <iframe
            src={activeCalendarUrl}
            title="Remote Calendar"
            width="100%"
            height="100%"
            frameBorder="0"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          />
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography variant="body1" color="text.secondary">
              No calendar URL configured for this consultation type.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Calendar;